import { useEffect, useRef, useState } from 'react';
import {
  Facets,
  FilterList,
  SearchDetails,
  SearchInput,
  SortOn,
  ViewSwitcher,
} from '@plone/volto/components/manage/Blocks/Search/components';
import cx from 'classnames';
import { flushSync } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Grid } from 'semantic-ui-react';

import styles from './ContainerFacet.module.less';

const messages = defineMessages({
  searchButtonText: {
    id: 'SearchAudioButtonText',
    defaultMessage: 'Search',
  },
  searchLabel: {
    id: 'SearchAudio',
    defaultMessage: 'Search Audio',
  },
  searchInputPrompt: {
    id: 'SearchAudioInputPrompt',
    defaultMessage: 'Search audio content',
  },
});

const FacetWrapper = ({ children, facetSettings = {}, visible }) => {
  const { advanced, field = {} } = facetSettings;

  return (
    <Grid.Column
      mobile={12}
      tablet={4}
      computer={3}
      className={cx('facet', {
        [`facet-index-${field.value}`]: !!field.value,
        'advanced-facet': advanced,
        'advanced-facet-hidden': !visible,
      })}
    >
      {children}
    </Grid.Column>
  );
};

const AudioFacet = (props) => {
  const {
    children,
    data,
    totalItems,
    facets,
    setFacets,
    setSortOn,
    setSortOrder,
    sortOn,
    sortOrder,
    onTriggerSearch,
    searchedText,
    searchText,
    setSearchText,
    isEditMode,
    querystring = {},
  } = props;
  const { showSearchButton } = data;
  const isLive = !showSearchButton;
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();
  const modalRef = useRef(null);
  const buttonRef = useRef(null);

  const classesLabel = 'text-semi-bold text-up-01 border-bottom mb-3 pb-1';

  const searchLabelText = intl.formatMessage(messages.searchLabel);
  const searchInputPromptText = intl.formatMessage(messages.searchInputPrompt);
  const searchButtonText = intl.formatMessage(messages.searchButtonText);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Grid className="searchBlock-facets audio" stackable>
      {data.headline && (
        <Grid.Row>
          <Grid.Column>
            <h2 className="headline">{data.headline}</h2>
          </Grid.Column>
        </Grid.Row>
      )}
      <div className={cx('col-12 mt-3', styles['container-filtros'])}>
        <button
          className={cx(
            'br-button filter-button terciary d-flex align-items-center justify-content-center toggle-button',
            styles['filter-button'],
          )}
          type="button"
          aria-label="Voltar página"
          onClick={() => {
            setIsOpen((prev) => !prev);
          }}
          ref={buttonRef}
        >
          <i
            className={cx('mr-1', {
              'fas fa-sliders': !isOpen,
              'fas fa-times': isOpen,
            })}
            aria-hidden="true"
          />
          <span>Filtrar</span>
        </button>
        <div
          ref={modalRef}
          className={cx(styles['janela-filtro'], {
            'd-none': !isOpen,
          })}
        >
          <div className="p-3">
            {(Object.keys(data).includes('showSearchInput')
              ? data.showSearchInput
              : true) && (
              <>
                <div
                  className={classesLabel}
                  htmlFor={`${props.id}-searchtext`}
                >
                  {searchLabelText}
                </div>
                <div className="search-wrapper">
                  <SearchInput
                    {...props}
                    data={{
                      ...data,
                      searchInputPrompt: searchInputPromptText,
                    }}
                    isLive={isLive}
                  />
                  {data.showSearchButton && (
                    <Button primary onClick={() => onTriggerSearch(searchText)}>
                      {data.searchButtonLabel || searchButtonText}
                    </Button>
                  )}
                </div>
              </>
            )}

            {data.facets?.length > 0 && (
              <div className="facets">
                {data.facetsTitle && (
                  <h3 className={classesLabel}>{data.facetsTitle}</h3>
                )}
                <Facets
                  data={data}
                  querystring={querystring}
                  facets={facets}
                  setFacets={(f) => {
                    flushSync(() => {
                      setFacets(f);
                      onTriggerSearch(searchedText || '', f);
                    });
                  }}
                  facetWrapper={FacetWrapper}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Grid.Row className="pb-0">
        <Grid.Column>
          <div className="search-filters-sort">
            <FilterList
              {...props}
              isEditMode={isEditMode}
              setFacets={(f) => {
                flushSync(() => {
                  setFacets(f);
                  onTriggerSearch(searchedText || '', f);
                });
              }}
              cleanFilters={() => {
                flushSync(() => {
                  setFacets({});
                  setSearchText('');
                  onTriggerSearch('', {});
                });
              }}
            />

            {data.showSortOn && (
              <SortOn
                data={data}
                querystring={querystring}
                isEditMode={isEditMode}
                sortOn={sortOn}
                sortOrder={sortOrder}
                setSortOn={(sortOn) => {
                  flushSync(() => {
                    setSortOn(sortOn);
                    onTriggerSearch(searchedText || '', facets, sortOn);
                  });
                }}
                setSortOrder={(sortOrder) => {
                  flushSync(() => {
                    setSortOrder(sortOrder);
                    onTriggerSearch(
                      searchedText || '',
                      facets,
                      sortOn,
                      sortOrder,
                    );
                  });
                }}
              />
            )}
            {data.availableViews && data.availableViews.length > 1 && (
              <ViewSwitcher {...props} />
            )}
          </div>

          <SearchDetails
            text={searchedText}
            total={totalItems}
            as="h2"
            className="text-semi-bold text-up-01"
            data={data}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className="pt-0">
        <Grid.Column>{children}</Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default AudioFacet;
